/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import {Nagbar} from '@app/components/layout/Nagbar';
import {NagbarButton} from '@app/components/layout/NagbarButton';
import {NagbarContent} from '@app/components/layout/NagbarContent';
import {
	dismissScheduledMaintenanceNagbar,
	isScheduledMaintenanceNagbarDismissed,
} from '@app/components/layout/app_layout/ScheduledMaintenanceDismissal';
import NagbarStore from '@app/stores/NagbarStore';
import StatusPageStore from '@app/stores/StatusPageStore';
import * as LocaleUtils from '@app/utils/LocaleUtils';
import {HOURS_PER_DAY, MINUTES_PER_HOUR} from '@fluxer/date_utils/src/DateConstants';
import {getFormattedDateTime} from '@fluxer/date_utils/src/DateFormatting';
import {ph} from '@lingui/core/macro';
import {Trans} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback, useMemo} from 'react';

interface MaintenanceScheduleLabels {
	startLabel: string;
	durationLabel: string;
}

function formatMaintenanceTimeLabel(value: string, locale: string): string {
	const parsedDate = new Date(value);
	if (Number.isNaN(parsedDate.getTime())) {
		return value;
	}
	return getFormattedDateTime(parsedDate, locale);
}

function formatMaintenanceDurationLabel(durationMinutes: number, locale: string): string {
	const normalizedDurationMinutes = Math.max(1, Math.round(durationMinutes));
	const durationParts =
		normalizedDurationMinutes % (HOURS_PER_DAY * MINUTES_PER_HOUR) === 0
			? {value: normalizedDurationMinutes / (HOURS_PER_DAY * MINUTES_PER_HOUR), unit: 'day'}
			: normalizedDurationMinutes % MINUTES_PER_HOUR === 0
				? {value: normalizedDurationMinutes / MINUTES_PER_HOUR, unit: 'hour'}
				: {value: normalizedDurationMinutes, unit: 'minute'};

	try {
		return new Intl.NumberFormat(locale, {
			style: 'unit',
			unit: durationParts.unit,
			unitDisplay: 'long',
		}).format(durationParts.value);
	} catch {
		const pluralSuffix = durationParts.value === 1 ? '' : 's';
		return `${durationParts.value} ${durationParts.unit}${pluralSuffix}`;
	}
}

export const ScheduledMaintenanceNagbar = observer(({isMobile}: {isMobile: boolean}) => {
	const scheduledMaintenance = StatusPageStore.scheduledMaintenance;
	const locale = LocaleUtils.getCurrentLocale();

	const scheduleLabels = useMemo<MaintenanceScheduleLabels | null>(() => {
		if (!scheduledMaintenance) {
			return null;
		}

		return {
			startLabel: formatMaintenanceTimeLabel(scheduledMaintenance.start, locale),
			durationLabel: formatMaintenanceDurationLabel(scheduledMaintenance.durationMinutes, locale),
		};
	}, [locale, scheduledMaintenance]);

	const handleOpenStatusPage = useCallback(() => {
		if (!scheduledMaintenance) {
			return;
		}

		window.open(scheduledMaintenance.url, '_blank', 'noopener,noreferrer');
	}, [scheduledMaintenance]);

	const handleDismiss = useCallback(() => {
		if (!scheduledMaintenance) {
			return;
		}

		dismissScheduledMaintenanceNagbar(scheduledMaintenance.id);
		NagbarStore.bumpScheduledMaintenanceDismissed();
	}, [scheduledMaintenance]);

	if (!scheduledMaintenance || !scheduleLabels || isScheduledMaintenanceNagbarDismissed(scheduledMaintenance.id)) {
		return null;
	}

	return (
		<Nagbar
			isMobile={isMobile}
			backgroundColor="#4b5563"
			textColor="#ffffff"
			dismissible
			onDismiss={handleDismiss}
		>
			<NagbarContent
				isMobile={isMobile}
				onDismiss={handleDismiss}
				message={
					<Trans comment="Maintenance banner message shown before a planned status-page maintenance begins. The start time and duration are already localized. Keep the tone plain.">
						Planned maintenance will start <strong>{ph({localizedTime: scheduleLabels.startLabel})}</strong> and last{' '}
						<strong>{ph({duration: scheduleLabels.durationLabel})}</strong>.
					</Trans>
				}
				actions={
					<NagbarButton isMobile={isMobile} onClick={handleOpenStatusPage}>
						<Trans comment="CTA on the planned-maintenance banner. Opens the external status-page maintenance notice.">
							Learn more
						</Trans>
					</NagbarButton>
				}
			/>
		</Nagbar>
	);
});
